import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Spinner, Select, SelectItem } from '@nextui-org/react';
import { DatePicker } from '@heroui/date-picker';
import { AuthAlert } from '../AuthAlert';
import { API_URL, TOAST_STYLE_SUCCESS } from '../../utils/constants';
import { ArrowUp, ArrowDown, Loader2, Trash2, X } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import auth from '../../Auth';
import Breadcrumbs from '../Breadcrumbs';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useInfiniteScroll } from '@nextui-org/use-infinite-scroll';
import { useAsyncList } from '@react-stately/data';
import { useLocation, useNavigate } from 'react-router-dom';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';

const Logs = () => {
  const [sortConfig, setSortConfig] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [delPromptId, setDelPromptId] = useState(-1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [filterPromptId, setFilterPromptId] = useState('');
  const [filterDatetime, setFilterDatetime] = useState('');
  const [value, setValue] = useState(null);
  const [logsIsLoading, setLogsIsLoading] = useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [cursorTokenEnvId, setCursorTokenEnvId] = useState('');
  const token = auth.syncGetIdToken();
  const [debouncedFilterPromptId] = useDebounce(filterPromptId, 300);

  const navigate = useNavigate();
  const location = useLocation();

  let list = useAsyncList({
    async load({ signal, cursor }) {
      const res = await axios.post(
        `${API_URL}/logs?getLogs${filterDatetime ? `&dateFilter=${filterDatetime}` : ''}`,
        {
          cursor: isFilterChanged ? '' : cursor || '',
          prompt_id: filterPromptId, // Ensure this is debounced before triggering `load`
          page_size: 50,
          dateFilter: Number(filterDatetime.replaceAll('-', ''))
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal
        }
      );
      const data = res.data;

      setLogsIsLoading(false);
      setHasMore(data.nextCursor !== null);

      const updatedLogs = data.items.map((log) => ({
        ...log,
        timestamp: log.timestamp ? parseInt(log.timestamp) : undefined,
        metrics: log.metrics
          ? {
            price_of_call: log.metrics.price_of_call ? parseFloat(log.metrics.price_of_call).toFixed(2) : 0.0,
            latency: log.metrics.latency ? parseFloat(log.metrics.latency).toFixed(2) : 0.0
          }
          : {}
      }));
      setIsFilterChanged(false);

      return {
        items: updatedLogs,
        cursor: data.nextCursor
      };
    },
    getKey: (item) => `${item.env_id}#${item.prompt_id}#${item.timestamp}`
  });

  // Track previous values to prevent duplicate reloads
  const prevFiltersRef = useRef({ promptId: '', dateFilter: '' });

  useEffect(() => {
    const { search, pathname } = location;
    const query = new URLSearchParams(search);

    // Update query parameters
    const updateQueryParam = (key, value) => {
      if (value) {
        query.set(key, value);
      } else {
        query.delete(key);
      }
    };

    updateQueryParam('promptId', debouncedFilterPromptId);
    updateQueryParam('dateFilter', filterDatetime);

    const newSearch = query.toString();
    const currentSearch = search.substring(1);

    // Prevent unnecessary navigation updates
    if (newSearch !== currentSearch) {
      navigate(`${pathname}?${newSearch}`, { replace: true });
    }

    // Only reload if filters have actually changed
    if (prevFiltersRef.current.promptId !== debouncedFilterPromptId || prevFiltersRef.current.dateFilter !== filterDatetime) {
      prevFiltersRef.current = { promptId: debouncedFilterPromptId, dateFilter: filterDatetime };
      list.reload();
    }
  }, [debouncedFilterPromptId, filterDatetime, location.search, location.pathname]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    setFilterPromptId(query.get('promptId') || '');
    setFilterDatetime(query.get('dateFilter') || '');
  }, []);

  const [loaderRef, scrollerRef] = useInfiniteScroll({ hasMore, onLoadMore: list.loadMore });

  const onChangeDateValue = (value) => {
    setFilterDatetime(`${value.year}${value.month < 10 ? `0${value.month}` : value.month}${value.day}`);
    setValue(value);
    setIsFilterChanged(true);
  };

  const handleRowClick = (id, timestamp) => {
    window.open(`/logs/log?prompt_id=${id}&timestamp=${timestamp}`);
  };

  const requestSort = (key, type = 'string') => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else {
      direction = 'ascending';
    }
    setSortConfig({ key, direction, type });
  };

  const getValueByPath = (object, path) => {
    return path.split('.').reduce((o, p) => (o ? o[p] : null), object);
  };

  const getSortDirectionIcon = (key) => {
    if (sortConfig && sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? <ArrowUp className='inline-block ml-2' /> : <ArrowDown className='inline-block ml-2' />;
    }
    return <ArrowDown className='inline-block ml-2 light-grey' />;
  };

  const deleteLocally = (key) => {
    list.remove(key);
  };

  const sortedLogs = useMemo(() => {
    if (!sortConfig) return list.items;

    return [...list.items].sort((a, b) => {
      let aValue = getValueByPath(a, sortConfig.key);
      let bValue = getValueByPath(b, sortConfig.key);

      if (aValue == null) aValue = sortConfig.type === 'integer' ? 0 : '';
      if (bValue == null) bValue = sortConfig.type === 'integer' ? 0 : '';

      if (sortConfig.type === 'integer') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  }, [list.items.slice(0, list.items.length), sortConfig]); // Only sort loaded data

  const uniquePromptIds = useMemo(() => Array.from(new Set(sortedLogs.map((item) => item.prompt_id ?? ''))), [sortedLogs]);

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/logs', breadcrumb: 'Logs' }
  ];

  const deleteLog = async (promptId, timestamp, id) => {
    setIsDeleting(true);
    setDelPromptId(id);
    await axios
      .delete(`${API_URL}/logs/${promptId}?timestamp=${timestamp}`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      })
      .then((res) => {
        deleteLocally(`${res.data['env_id']}#${res.data['prompt_id']}#${res.data['timestamp']}`);
        toast.success(`Log is deleted!`, TOAST_STYLE_SUCCESS);
      })
      .catch((err) => console.log(err));
    setIsDeleting(false);
    setDelPromptId(-1);
  };

  function formatDateTime(input) {
    const date = new Date(input);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${hours}:${minutes}:${seconds} ${day}.${month}.${year}`;
  }

  const xLabels = sortedLogs.map(item => formatDateTime(item.created_dtf));
  const latencyData = sortedLogs.map(item => item.metrics.latency / 1000);

  const series = [
    {
      name: 'Latency',
      data: latencyData,
    },
  ];

  const latencyByPrompt = {};
  sortedLogs.forEach(item => {
    const promptId = item.prompt_id;
    const latency = item.metrics.latency / 1000;
    if (!latencyByPrompt[promptId]) {
      latencyByPrompt[promptId] = { total: 0, count: 0 };
    }
    latencyByPrompt[promptId].total += latency;
    latencyByPrompt[promptId].count += 1;
  });

  const averageLatencies = Object.entries(latencyByPrompt).map(([promptId, { total, count }]) => ({
    promptId,
    average: (total / count).toFixed(2),
  }));

  function trimLabels(labels) {
    return labels.map(label => label.length > 15 ? label.substring(0, 15) + '...' : label);
  }

  const promptLabels = averageLatencies.map(item => item.promptId);
  const trimmedLabels = trimLabels(promptLabels);

  const nameMapping = {};
  averageLatencies.forEach(item => {
    const trimmedName = trimLabels([item.promptId])[0];
    nameMapping[trimmedName] = item.promptId;
  });
  const averageLatencyValues = averageLatencies.map(item => parseFloat(item.average));

  return (
    <div className='flex flex-col w-full p-8 space-y-8'>
      <Breadcrumbs crumbs={crumbs} />
      <div className='flex w-full'>
        <div className='w-1/2'>
          <h3 className='col-span-4 text-2xl font-bold'>Latency over time:</h3>
          <LineChart
            xAxis={[{ scaleType: 'point', data: xLabels }]}
            series={series}
            height={400}
          />
        </div>
        <div className='w-1/2'>
          <h3 className='col-span-4 text-2xl font-bold'>Average Latency by Prompt ID:</h3>
          <BarChart
            xAxis={[{
              scaleType: 'band',
              data: trimmedLabels,
              tickPlacement: 'middle',
              tickLabelPlacement: 'middle',
              valueFormatter: (code, context) =>
                context.location === 'tick'
                  ? code
                  : `${nameMapping[code]}`
            }]}
            series={[{ data: averageLatencyValues }]}
            height={400}
          />
        </div>
      </div>
      <div className='col-span-2 grid grid-cols-1 gap-4'>
        <h1 className='col-span-4 text-2xl font-bold'>Logs:</h1>
      </div>
      {showAlert && <AuthAlert />}
      <Table
        className='max-h-[75vh]'
        aria-label='Prompt Library Table'
        isHeaderSticky
        baseRef={scrollerRef}
        bottomContent={
          hasMore ? (
            <div className='flex w-full justify-center'>
              <Spinner ref={loaderRef} color='black' />
            </div>
          ) : null
        }
      >
        <TableHeader>
          <TableColumn key='prompt_id'>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  requestSort('prompt_id', 'integer');
                }}
              >
                Prompt id {getSortDirectionIcon('prompt_id')}
              </div>
              <Select
                label='Select Prompt ID'
                selectedKeys={filterPromptId ? [filterPromptId] : []}
                onSelectionChange={(keys) => {
                  setFilterPromptId(keys.values().next().value);
                  setIsFilterChanged(true);
                }}
                className='w-64'
                style={{ pointerEvents: 'auto' }}
              >
                <SelectItem key='' value=''>
                  All
                </SelectItem>
                {uniquePromptIds.map((id) => (
                  <SelectItem key={id} value={id}>
                    {id}
                  </SelectItem>
                ))}
              </Select>
              {filterPromptId && (
                <X
                  style={{ width: 40, cursor: 'pointer' }}
                  onClick={() => {
                    setFilterPromptId('');
                    setIsFilterChanged(true);
                  }}
                />
              )}
            </div>
          </TableColumn>
          <TableColumn onClick={() => requestSort('response')}>Response</TableColumn>
          <TableColumn onClick={() => requestSort('metrics.price_of_call', 'integer')}>
            Price, ${getSortDirectionIcon('metrics.price_of_call')}
          </TableColumn>
          <TableColumn onClick={() => requestSort('metrics.latency', 'integer')}>
            Latency, s{getSortDirectionIcon('metrics.latency')}
          </TableColumn>
          <TableColumn>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  requestSort('timestamp');
                }}
              >
                Datetime {getSortDirectionIcon('timestamp')}
              </div>
              <DatePicker value={value} onChange={onChangeDateValue} />
            </div>
          </TableColumn>
          <TableColumn>
            {filterDatetime && (
              <X
                style={{ width: 40, cursor: 'pointer' }}
                onClick={() => {
                  setFilterDatetime('');
                  setValue(null);
                  setIsFilterChanged(true);
                }}
              />
            )}
          </TableColumn>
        </TableHeader>
        <TableBody isLoading={logsIsLoading}>
          {sortedLogs.length > 0 ? (
            sortedLogs.map((item, index) => (
              <TableRow className='hover:bg-gray-200 cursor-pointer' key={index}>
                <TableCell onClick={() => handleRowClick(item.prompt_id, item.timestamp)} className='w-1/5'>
                  {item.prompt_id} {item.tag}
                </TableCell>
                <TableCell>{item.response?.content}</TableCell>
                <TableCell>{item.metrics.price_of_call}</TableCell>
                <TableCell>{item.metrics.latency / 1000}</TableCell>
                <TableCell>{formatDateTime(item.created_dtf)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {!isDeleting || delPromptId !== index ? (
                    <Trash2 color='red' size={25} cursor={'pointer'} onClick={() => deleteLog(item.prompt_id, item.timestamp, index)} />
                  ) : (
                    isDeleting && delPromptId === index && <Loader2 size={25} color='black' className='animate-spin' />
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell>{!sortedLogs.length && !hasMore ? 'Nothing was found for your request' : ''}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default Logs;